import Link from 'next/link';
import Badge, { type BadgeColor } from '@src/components/atoms/badges/badge';
import type { DRepSearchHit, DRepStatus } from '@src/lib/seo/catalog';
import { sanitizeHighlight } from '@src/lib/seo/highlight';
import type { ProposalState } from '@src/lib/seo/proposal';
import { searchLabel } from '@src/constants/searchLabel';
import { getValueByPath } from '@src/utils/metadataUtils';

/**
 * Server-rendered cards for the crawlable directory and hub pages.
 *
 * Deliberately free of client JavaScript and built on real `<a>` elements.
 * The existing landing-page cards navigate with `onClick` + `router.push`,
 * which leaves nothing for a crawler to follow even after JS runs.
 *
 * Styling comes from the same atoms the interactive pages use — the `Badge`
 * atom rather than a second set of status colours, and the app's type scale
 * rather than raw Tailwind sizes.
 */

/** Same mapping the governance actions table uses: live is green, gone is red. */
export const BADGE_COLOR_BY_STATE: Record<ProposalState, BadgeColor> = {
  active: 'success',
  enacted: 'information',
  ratified: 'information',
  expired: 'error',
  dropped: 'error',
};

const CARD_SURFACE =
  'group flex h-full flex-col rounded-lg border border-gray-200 bg-white p-4 transition-colors hover:border-gray-900 focus-visible:outline focus-visible:outline-2 focus-visible:outline-brand dark:border-dark-neutral-700 dark:bg-dark-neutral-900 dark:hover:border-white';

/** Same mapping the search card uses: active is green, retired is red. */
const BADGE_COLOR_BY_DREP_STATUS: Record<DRepStatus, BadgeColor> = {
  Active: 'success',
  Inactive: 'warning',
  Retired: 'error',
};

export function DRepCard({
  href,
  name,
  summary,
  image,
  status,
  drepIdCip105,
  highlights,
}: {
  href: string;
  name: string;
  summary?: string | null;
  status?: DRepStatus | null;
  /** Shown as the identifying line on search results, as the popup does. */
  drepIdCip105?: string | null;
  /** Matched fields from the indexed search, shown instead of the summary. */
  highlights?: DRepSearchHit['highlights'];
  /**
   * Already resolved by `avatarUrl()` — an `http(s)` URL or a
   * `/drep-avatar/<id>` path, never a raw `data:` URI. Callers must not pass
   * `record.image` straight through: 60 of these render per directory page,
   * and inlining base64 there was worth hundreds of KB of HTML.
   */
  image?: string | null;
}) {
  const searching = Boolean(highlights && highlights.length > 0);
  // A match on the name is shown on the name itself rather than repeated as a
  // row underneath it.
  const nameHighlight = highlights?.find((entry) =>
    entry.field.startsWith('givenName')
  );
  const fieldHighlights =
    highlights?.filter((entry) => !entry.field.startsWith('givenName')) ?? [];

  return (
    <Link href={href} className={`${CARD_SURFACE} gap-3`}>
      <div className="flex items-center gap-3">
        {/* eslint-disable-next-line @next/next/no-img-element */}
        <img
          src={image || '/dReps/default-dRep-profile.png'}
          alt=""
          width={44}
          height={44}
          loading="lazy"
          // The host is chosen by the DRep operator, so don't hand it the
          // visitor's referring URL.
          referrerPolicy="no-referrer"
          className="h-11 w-11 shrink-0 rounded-full object-cover"
        />
        <div className="min-w-0 flex-1">
          {nameHighlight ? (
            <h2
              className="search-value body16 truncate font-semibold text-gray-900 dark:text-white"
              // Escaped in `sanitizeHighlight`; only the `<em>` marking the
              // match survives.
              dangerouslySetInnerHTML={{
                __html: sanitizeHighlight(nameHighlight.snippets[0]),
              }}
            />
          ) : (
            <h2 className="body16 truncate font-semibold text-gray-900 dark:text-white">
              {name}
            </h2>
          )}
          {/*
            No `@handle` line. A handle is minted under DREP_POLICY and the
            Elasticsearch catalog carries no handle field, so nothing available
            here is an owned identifier. Rendering the self-declared `dRepName`
            with an `@` presented one DRep's claim as another's identity.
          */}
        </div>
        {status && (
          <Badge
            className="shrink-0"
            label={status}
            color={BADGE_COLOR_BY_DREP_STATUS[status]}
          />
        )}
      </div>

      {searching ? (
        <dl className="flex flex-col gap-1.5 border-t border-gray-200 pt-3 dark:border-dark-neutral-700">
          {drepIdCip105 && (
            <Field label="DRep ID (CIP 105)" value={drepIdCip105} />
          )}
          {fieldHighlights.slice(0, 3).map((entry) => (
            <Field
              key={entry.field}
              label={getValueByPath(searchLabel, entry.field)}
              html={sanitizeHighlight(entry.snippets[0])}
            />
          ))}
        </dl>
      ) : (
        summary && (
          <p className="body14 line-clamp-3 font-medium text-gray-600 dark:text-gray-400">
            {summary}
          </p>
        )
      )}
    </Link>
  );
}

/** Label above value, so a long id or snippet has the full card width. */
function Field({
  label,
  value,
  html,
}: {
  label: string;
  value?: string;
  html?: string;
}) {
  return (
    <div className="flex flex-col gap-0.5">
      <dt className="label12 font-semibold text-gray-700 dark:text-gray-300">
        {label}
      </dt>
      {html ? (
        <dd
          className="search-value label12 line-clamp-2 font-medium text-gray-600 dark:text-gray-400"
          dangerouslySetInnerHTML={{ __html: html }}
        />
      ) : (
        <dd className="label12 truncate font-medium text-gray-600 dark:text-gray-400">
          {value}
        </dd>
      )}
    </div>
  );
}

export function ProposalCard({
  href,
  title,
  facts,
  abstract,
  color,
  stateLabel,
}: {
  href: string;
  title: string;
  facts: string;
  abstract?: string | null;
  color: BadgeColor;
  stateLabel: string;
}) {
  return (
    <Link href={href} className={`${CARD_SURFACE} gap-2`}>
      <Badge className="w-fit" label={stateLabel} color={color} />
      <h2 className="body16 line-clamp-2 font-semibold text-gray-900 dark:text-white">
        {title}
      </h2>
      <p className="label12 font-medium text-gray-500 dark:text-gray-500">
        {facts}
      </p>
      {abstract && (
        <p className="body14 line-clamp-3 font-medium text-gray-600 dark:text-gray-400">
          {abstract}
        </p>
      )}
    </Link>
  );
}
